import { jsPDF } from "jspdf";
import type { ChildSchoolFees, ParentChild } from "../types/parentFamily";
import { getChildFullName } from "./parentFamilyHelpers";
import {
  buildFeeLedger,
  dayTypeLabel,
  formatLedgerCurrency,
  formatLedgerDate,
  paymentPlanLabel,
  summarizeSchoolFees,
  type SchoolFeeContactInfo,
} from "./schoolFeesHelpers";

const TEXT = "#111B21";
const MUTED = "#667781";
const BORDER = "#333333";
const HEADER_BG = "#F5F5F5";
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;

type LogoImage = {
  dataUrl: string;
  width: number;
  height: number;
};

function pdfSafeText(value: string): string {
  return value
    .replace(/\u00a0/g, " ")
    .replace(/\u202f/g, " ")
    .replace(/\u2013|\u2014/g, "-")
    .replace(/[^\x20-\x7E\u00A0-\u00FF]/g, "?")
    .trim();
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    Number.parseInt(h.slice(0, 2), 16),
    Number.parseInt(h.slice(2, 4), 16),
    Number.parseInt(h.slice(4, 6), 16),
  ];
}

function isUsableLogoUrl(url?: string): url is string {
  if (!url?.trim()) return false;
  const trimmed = url.trim();
  if (trimmed === "/placeholder.jpg") return false;
  return (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("/")
  );
}

async function loadLogoImage(url: string): Promise<LogoImage | null> {
  try {
    if (url.startsWith("data:")) {
      return await normalizeLogoToPng(url);
    }
    const absolute =
      url.startsWith("/") && typeof window !== "undefined"
        ? `${window.location.origin}${url}`
        : url;
    const response = await fetch(absolute, {
      mode: "cors",
      credentials: "omit",
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    return await normalizeLogoToPng(dataUrl);
  } catch {
    return null;
  }
}

async function normalizeLogoToPng(dataUrl: string): Promise<LogoImage | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const width = img.naturalWidth || 1;
        const height = img.naturalHeight || 1;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width,
          height,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function fitLogoSize(
  logo: LogoImage,
  maxW: number,
  maxH: number
): { w: number; h: number } {
  const ratio = logo.width / logo.height || 1;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return { w, h };
}

function drawCellBorder(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const [r, g, b] = hexToRgb(BORDER);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.25);
  doc.rect(x, y, w, h);
}

function drawCellText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: { align?: "left" | "center" | "right"; bold?: boolean; size?: number }
): void {
  const align = opts?.align || "left";
  const size = opts?.size || 9;
  doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
  doc.setFontSize(size);
  doc.setTextColor(TEXT);
  const pad = 2;
  const tx =
    align === "center"
      ? x + w / 2
      : align === "right"
        ? x + w - pad
        : x + pad;
  doc.text(pdfSafeText(text), tx, y + h / 2 + size / 8 - 1, { align });
}

export interface FeeStatementPdfOptions {
  organizationName?: string;
  organizationLogoUrl?: string;
  contact?: SchoolFeeContactInfo;
}

/**
 * Builds an annual fee ledger PDF matching the school's statement format.
 */
export async function buildFeeStatementPdfBlob(
  child: Pick<
    ParentChild,
    "firstName" | "lastName" | "grade" | "classSection" | "studentId"
  >,
  account: ChildSchoolFees,
  options?: FeeStatementPdfOptions
): Promise<{ blob: Blob; fileName: string }> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const organizationName = options?.organizationName?.trim() || "";
  const childName = getChildFullName(child) || "Student";
  const gradeLabel = child.grade?.trim() || "—";
  const contentWidth = PAGE_W - MARGIN * 2;
  const summary = summarizeSchoolFees(account);
  const ledger = buildFeeLedger(account);

  const logoUrl = options?.organizationLogoUrl;
  const logo = isUsableLogoUrl(logoUrl) ? await loadLogoImage(logoUrl) : null;

  let y = MARGIN;

  // —— Header: logo + org name | contact details ——
  const headerH = 28;
  const contactX = PAGE_W - MARGIN - 58;

  if (logo) {
    const { w, h } = fitLogoSize(logo, 22, 22);
    try {
      doc.addImage(logo.dataUrl, "PNG", MARGIN, y, w, h);
    } catch {
      // skip
    }
  }

  if (organizationName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor("#1565C0");
    doc.text(pdfSafeText(organizationName.toUpperCase()), MARGIN + 26, y + 8);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  const contact = options?.contact;
  let contactY = y + 2;
  if (contact?.phone) {
    doc.text(`Tel: ${pdfSafeText(contact.phone)}`, contactX, contactY);
    contactY += 4;
  }
  if (contact?.email) {
    doc.text(`Email: ${pdfSafeText(contact.email)}`, contactX, contactY);
    contactY += 4;
  }
  if (contact?.website) {
    doc.text(`Web: ${pdfSafeText(contact.website)}`, contactX, contactY);
  }

  y += headerH;

  // —— Student summary table ——
  const summaryRows: [string, string][] = [
    ["Child name", childName],
    ["Grade", gradeLabel],
    ["Full day/Half Day", dayTypeLabel(account.dayType)],
    ["Payment Plan", paymentPlanLabel(account.paymentPlanMonths)],
    [
      `Total fee for ${account.academicYear}`,
      formatLedgerCurrency(account.totalAnnualFee, account.currency),
    ],
  ];

  const labelW = 52;
  const valueW = contentWidth - labelW;
  const summaryRowH = 7;

  for (const [label, value] of summaryRows) {
    drawCellBorder(doc, MARGIN, y, labelW, summaryRowH);
    drawCellBorder(doc, MARGIN + labelW, y, valueW, summaryRowH);
    drawCellText(doc, label, MARGIN, y, labelW, summaryRowH, { bold: true });
    drawCellText(doc, value, MARGIN + labelW, y, valueW, summaryRowH);
    y += summaryRowH;
  }

  y += 4;

  // —— Payment ledger table ——
  const colWidths = [28, 22, 38, 42, 38];
  const tableW = colWidths.reduce((a, b) => a + b, 0);
  const tableX = MARGIN + (contentWidth - tableW) / 2;
  const rowH = 7;

  const headers = [
    "Month",
    "Date",
    "Amount paid",
    "Amount outstanding",
    "Receipt No.",
  ];

  const [hr, hg, hb] = hexToRgb(HEADER_BG);
  doc.setFillColor(hr, hg, hb);
  doc.rect(tableX, y, tableW, rowH, "F");

  let colX = tableX;
  for (let i = 0; i < headers.length; i++) {
    drawCellBorder(doc, colX, y, colWidths[i], rowH);
    drawCellText(doc, headers[i], colX, y, colWidths[i], rowH, {
      bold: true,
      align: "center",
      size: 8,
    });
    colX += colWidths[i];
  }
  y += rowH;

  for (const row of ledger) {
    const values = [
      row.monthLabel,
      row.paidDate ? formatLedgerDate(row.paidDate) : "",
      row.amountPaid != null
        ? formatLedgerCurrency(row.amountPaid, account.currency)
        : "",
      row.amountOutstanding != null
        ? formatLedgerCurrency(row.amountOutstanding, account.currency)
        : "",
      row.receiptNo ? `Receipt ${row.receiptNo}` : "",
    ];

    colX = tableX;
    for (let i = 0; i < values.length; i++) {
      drawCellBorder(doc, colX, y, colWidths[i], rowH);
      drawCellText(doc, values[i], colX, y, colWidths[i], rowH, {
        align: i === 0 ? "left" : "center",
        size: 8,
      });
      colX += colWidths[i];
    }
    y += rowH;
  }

  // —— Amount due footer ——
  y += 2;
  const footerLabelW = colWidths[0] + colWidths[1] + colWidths[2];
  const footerValueW = colWidths[3] + colWidths[4];
  drawCellBorder(doc, tableX, y, footerLabelW, rowH);
  drawCellBorder(doc, tableX + footerLabelW, y, footerValueW, rowH);
  drawCellText(doc, "Amount Due", tableX, y, footerLabelW, rowH, {
    bold: true,
    align: "right",
  });
  const dueLabel = summary.isSettled
    ? "Paid up"
    : formatLedgerCurrency(summary.outstanding, account.currency);
  drawCellText(
    doc,
    dueLabel,
    tableX + footerLabelW,
    y,
    footerValueW,
    rowH,
    { bold: true, align: "center" }
  );

  // —— Footer note ——
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED);
  doc.text(
    pdfSafeText(
      organizationName
        ? `${organizationName} · Please contact the office with any queries.`
        : "Please contact the school office with any queries."
    ),
    PAGE_W / 2,
    PAGE_H - 12,
    { align: "center" }
  );

  const safeName = childName.replace(/[^a-zA-Z0-9]+/g, "-").slice(0, 30);
  const fileName = `fee-statement-${safeName}-${account.academicYear}.pdf`;

  return {
    blob: doc.output("blob"),
    fileName,
  };
}

export async function downloadFeeStatementPdf(
  child: Pick<
    ParentChild,
    "firstName" | "lastName" | "grade" | "classSection" | "studentId"
  >,
  account: ChildSchoolFees,
  options?: FeeStatementPdfOptions
): Promise<void> {
  const { blob, fileName } = await buildFeeStatementPdfBlob(
    child,
    account,
    options
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
